import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormGroup, FormBuilder } from '@angular/forms';
import { RoomService, IAvailableRoomsParams } from 'src/app/shared/services/rest/room.service';
import { Room } from 'src/app/shared/models/room.model';
import {
  CreateReservationDateComponent,
  IReservationDateFormModel
} from '../create-reservation-date/create-reservation-date.component';
import { ReservationService } from 'src/app/shared/services/rest/reservation.service';
import { Reservation } from 'src/app/shared/models/reservation';
import { Occupancy } from 'src/app/shared/models/occupancy';
import { DateHelper } from 'src/app/shared/utils/date-helper';
import { MessageService } from 'src/app/shared/services/message.service';
import { MessageType } from 'src/app/shared/models/message';
import { HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { OccupancyService } from 'src/app/shared/services/rest/occupancy.service';
import { LanguageService } from 'src/app/shared/services/language.service';
import { TranslateService } from '@ngx-translate/core';
import { HttpHelper } from 'src/app/shared/utils/http-helper';
import { CreateReservationDialogComponent } from './create-reservation-dialog-base.component';
import { ResourceType } from 'src/app/shared/models/resource-type';

@Component({
  selector: 'app-create-room-reservation-dialog',
  templateUrl: './create-reservation-dialog.component.html',
  styleUrls: ['./create-reservation-dialog.component.scss']
})
export class CreateRoomReservationDialogComponent extends CreateReservationDialogComponent implements OnInit {

  get type(): ResourceType {
    return ResourceType.Raum;
  }

  constructor(
    private roomService: RoomService,
    reservationService: ReservationService,
    occupancyService: OccupancyService,
    messages: MessageService,
    translate: TranslateService,
    language: LanguageService,
    fb: FormBuilder,
    protected readonly cdr: ChangeDetectorRef,
    ) {
    super(reservationService,
      occupancyService,
      messages,
      translate,
      language,
      fb,
      cdr);
  }

  protected resourceLabelWithArticle: string;

  ngOnInit() {
    this.createForm();
    this.loadData();
    this.loadOccupanciesData(this.params.from, this.params.to)
      .then(room => {
        this.resourceOccupancies = this.generateResourceOccupancies(room.Occupancies);
      });
    this.resolveArticleLabel('THE_ROOM');
  }

  /**
   * Load data from server
   *
   * @private
   * @memberof CreateReservationDialogComponent
   */
  protected async loadData() {
    this.isLoading = true;

    const reservationObserveable = this.isNewReservation ?
      this.reservationService.getTemplate(String(this.params.idRessource))
      : this.reservationService.get(String(this.params.idReservation));

    // get Reservation
    reservationObserveable.toPromise().then(
      (data: Reservation | HttpResponse<Reservation>) => {
        const reservationData = HttpHelper.getResponseData(data);
        this.reservation = Reservation.create(reservationData);

        // set reservationinfo for existing reservation
        if (!this.isNewReservation) {
          this.reservationInfo = { header: null, primary: this.reservation.StatusRemark, secondary: null };

          this.translate.get('STATE').subscribe((text: string) => {
            this.reservationInfo.header = text + ': ' + this.reservation.Status;
          });

          const condition = String(Number(this.reservation.hasFutureSavedDates())) + String(Number(this.reservation.IsEditable));
          let key: string;
          switch (condition) {
            case '11':
              key = 'RESERVATION_STATE_EDITABLE';
              break;
            case '00':
            case '01':
              key = 'RESERVATION_STATE_PAST';
              break;
            default:
              key = 'RESERVATION_STATE_READONLY';
          }

          this.translate.get(key).subscribe((text: string) => {
            this.reservationInfo.secondary = text;
          });
        }

        // get Roominfo
        this.roomService.get(String(this.params.idRessource)).toPromise().then(
          (roomData: Room | HttpResponse<Room>) => {
            this.initLoadedDates(HttpHelper.getResponseData(roomData));
            this.isLoading = false;
          },
          (error: HttpErrorResponse) => {
            this.initLoadedDates(null);
            this.handleRequestError(error, 'ROOM', this.params.idRessource);
            this.isLoading = false;
          }
        );

      },
      (error: HttpErrorResponse) => {
        this.handleRequestError(error, 'RESERVATION', this.params.idReservation);
        this.isLoading = false;
      }
    );

    // load reservationinfo for new reservation
    if (this.isNewReservation) {
      this.loadReservationsrecht();
    }
  }

  protected loadReservationsrecht() {
    const spanParams = this.params;
    const params: IAvailableRoomsParams = {
      availableFrom: spanParams.from,
      availableTo: spanParams.to,
      idRoom: [this.params.idRessource],
      availableOnly: false,
      buildings: [],
      equipments: [],
      floors: [],
      locations: [],
      ressourceNumber: [],
      roomTypes: [],
      seats: [],
      timespan: null
    };

    this.roomService.getAvailableRooms(params).toPromise().then(
      (data: any) => {
        if (data[0] && data[0].ReservationRightId) {
          this.reservationInfo = { header: null, primary: null, secondary: null };

          this.translate.get('ROOM_RESERVATIONRIGHT_HINT_HEADER_' + data[0].ReservationRightId).subscribe((text: string) => {
            this.reservationInfo.header = text;
          });
          this.translate.get('ROOM_RESERVATIONRIGHT_HINT_BODY_' + data[0].ReservationRightId).subscribe((text: string) => {
            this.reservationInfo.primary = text;
          });
        }
      },
      (error: HttpErrorResponse) => {
        this.messages.add(error.message, MessageType.Debug);
      }
    );

  }

  /**
   * Create FormGroup for CreateReservationDateComponent from Occupancy
   *
   * @param {Occupancy} o
   * @returns {FormGroup}
   * @memberof CreateReservationDialogComponent
   */
  createDateFormGroup(o: Occupancy): FormGroup {
    const group = CreateReservationDateComponent.createFormGroupModel(this.fb);
    group.setValue({
      id: o.LessonId,
      date: o.DateTimeFrom,
      dateTimeFrom: String(DateHelper.getMinutesOfDay(o.DateTimeFrom)),
      dateTimeTo: String(DateHelper.getMinutesOfDay(o.DateTimeTo)),
      message: ''
    });

    if (!o.IsEditable) {
      group.disable();
    }

    return group;
  }

  /**
   * Apply loaded data to form
   *
   * @private
   * @param {Room} room
   * @memberof CreateReservationDialogComponent
   */
  private initLoadedDates(room: Room) {
    // room data if set
    this.form.formGroup.patchValue({
      roomNumber: room ? room.Designation : '',
      seats: room ? room.NumberPersons : '',
      title: this.reservation.Designation,
      furtherDetails: this.reservation.FurtherInformation
    });

    if (this.isNewReservation) {
      this.initPresetDate();
    } else {
      this.reservation.Occupancies.forEach((o: Occupancy) => {
        const group = CreateReservationDateComponent.createFormGroupModel(this.fb);
        group.setValue({
          id: o.LessonId,
          date: o.DateTimeFrom.toISOString(),
          dateTimeFrom: String(DateHelper.getMinutesOfDay(o.DateTimeFrom)),
          dateTimeTo: String(DateHelper.getMinutesOfDay(o.DateTimeTo)),
          message: ''
        } as IReservationDateFormModel);

        if (!o.IsEditable) {
          group.disable();
        }

        this.dates.push(group);
      });
    }

    this.initReadonlyState();
  }

  public prepLoadOccupanciesData(idRessource: number) {
    const $parentThis = this;

    const loaderScope = (from: Date, to: Date) => {
      const params: IAvailableRoomsParams = {
        idRoom: [idRessource],
        availableFrom: from,
        availableTo: to,
        availableOnly: false,
        timespan: null,
        ressourceNumber: [],
        buildings: [],
        equipments: [],
        floors: [],
        locations: [],
        roomTypes: [],
        seats: [],
      };

      // return loader(params).toPromise().then(payload => payload[0] || []);
      return $parentThis.roomService.getAvailableRooms(params)
        .toPromise()
        .then(payload => Array.isArray(payload) ? payload[0] : payload)
        .then(room => room ? $parentThis.generateResourceOccupancies(room.Occupancies) : null);
    };

    return loaderScope;
  }

  public async loadOccupanciesData(from: Date, to: Date) {
    const params: IAvailableRoomsParams = {
      idRoom: [this.params.idRessource],
      availableFrom: from,
      availableTo: to,
      availableOnly: false,
      timespan: null,
      ressourceNumber: [],
      buildings: [],
      equipments: [],
      floors: [],
      locations: [],
      roomTypes: [],
      seats: [],
    };

    return await this.roomService.getAvailableRooms(params)
      .toPromise()
      .then(payload => Array.isArray(payload) ? payload[0] : payload);
  }
}
